<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import PrivacyConsent from '@/components/business/privacy-consent/index.vue';
import { privacyStatus } from '@/service/privacy';
import { useAppStore, useTokenStore, useUserStore } from '@/store';
import { useThemeStore } from '@/store/modules/theme';
import FgTabbar from '@/tabbar/index.vue';
import { isPageTabbar } from './tabbar/store';
import { currRoute, getAllPages } from './utils';

const appStore = useAppStore();
const isCurrentPageTabbar = ref(true);
const allPages = getAllPages();

const router = ref('');
const page = computed(() => {
  return allPages.find(page => page.path === router.value);
});
const pageMeta = computed(() => {
  return {
    path: page.value?.path || '',
    customNavigation: page.value?.style?.navigationStyle === 'custom',
    needLogin: page.value?.needLogin ?? false,
    roles: page.value?.roles ?? '',
  };
});
const pageStatus = ref<'loading' | 'success' | 'no-login' | 'forbidden'>('success');
const tokenStore = useTokenStore();
const userStore = useUserStore();
/* 权限检测 */
const checkAuth = () => {
  // 1. 当前需要登录获取需要权限使用，检测是否已登录
  if ((pageMeta.value.needLogin || pageMeta.value.roles) && !tokenStore.updateNowTime().hasLogin) {
    // 当前没有登录，要去登录
    pageStatus.value = 'no-login';
    return;
  }

  // 2. 当前需要角色检测
  if (pageMeta.value.roles && !userStore.perms.includes(pageMeta.value.roles)) {
    // 需要权限
    pageStatus.value = 'forbidden';
    return;
  }

  // 4. 登录及权限都通过
  pageStatus.value = 'success';
};

/* 主题相关配置 */
const themeStore = useThemeStore();
const { themeVars, theme } = storeToRefs(themeStore);

onShow(() => {
  console.log('App.ku.vue onShow', currRoute());
  const { path } = currRoute();
  // “蜡笔小开心”提到本地是 '/pages/index/index'，线上是 '/' 导致线上 tabbar 不见了
  // 所以这里需要判断一下，如果是 '/' 就当做首页，也要显示 tabbar
  if (path === '/') {
    isCurrentPageTabbar.value = true;
  }
  else {
    isCurrentPageTabbar.value = isPageTabbar(path);
  }
  // 获取页面路径，并进行登录和权限检测。当前为二次保险检测以及在调整前进行了拦截，为了保证没问题在页面显示的时候在进行一次检测
  router.value = path;
  checkAuth();
  console.log('当前页面状态:', router.value, pageStatus.value);
});

const exposeRef = ref('this is form app.Ku.vue');

defineExpose({
  exposeRef,
});
</script>

<template>
  <wd-config-provider class="flex" style="height: 100%; width: 750rpx" :theme-vars="themeVars" :theme="theme">
    <!-- 配置未加载时显示 loading 遮罩 -->
    <view v-if="!appStore.isConfigLoaded" class="config-loading-mask">
      <view class="loading-content">
        加载中...
      </view>
    </view>

    <!-- 首启隐私同意弹窗（iOS 工作台包；未同意前不加载配置、不初始化 SDK） -->
    <PrivacyConsent v-if="privacyStatus === 'pending'" />

    <template v-else>
      <AuthGuardWrapper :need-login="pageMeta.needLogin" :required-role="pageMeta.roles">
        <slot />
      </AuthGuardWrapper>

      <FgTabbar v-if="isCurrentPageTabbar" />
      <wd-toast direction="vertical" :z-index="1000" />
      <wd-message-box />
    </template>
  </wd-config-provider>
</template>

<style lang="scss" scoped>
.wot-theme-light {
  background-color: $color-gray-2;
}

.wot-theme-dark {
  color: $dark-color;
  background-color: $dark-background;
}

.config-loading-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
}

.loading-content {
  font-size: 28rpx;
  color: #999;
}
</style>
